import React, { useState, useEffect, useCallback, useMemo } from "react";
import useEmpresa from "../../../hooks/useEmpresa";
import {
  ModalContainer,
  ModalContenido,
  ModalHeader,
  ModalTitulo,
  ModalCerrarBtn,
  ModalDocumentoContainer,
  ModalImagen,
  ModalPdfContainer,
  ModalPdfIframe,
  ModalMensaje,
  ModalIcono,
  ModalTexto,
  ModalDescargaBtn,
} from "../styles/HojaQuirurgicaFormStyles";

// Constantes
const SUPPORTED_FILE_TYPES = {
  IMAGE: ['image/png', 'image/jpeg', 'image/jpg'],
  PDF: ['application/pdf'],
};

const SUPPORTED_EXTENSIONS = {
  IMAGE: ['png', 'jpg', 'jpeg'],
  PDF: ['pdf'],
};

const FILE_TYPE_LABELS = {
  imagen: 'imagen',
  pdf: 'pdf',
  no_soportado: 'no_soportado',
};

// Utilidades
const getFileTypeFromMimeType = (mimeType) => {
  if (SUPPORTED_FILE_TYPES.IMAGE.includes(mimeType)) return FILE_TYPE_LABELS.imagen;
  if (SUPPORTED_FILE_TYPES.PDF.includes(mimeType)) return FILE_TYPE_LABELS.pdf;
  return FILE_TYPE_LABELS.no_soportado;
};

const getFileTypeFromExtension = (extension) => {
  if (SUPPORTED_EXTENSIONS.IMAGE.includes(extension)) return FILE_TYPE_LABELS.imagen;
  if (SUPPORTED_EXTENSIONS.PDF.includes(extension)) return FILE_TYPE_LABELS.pdf;
  return FILE_TYPE_LABELS.no_soportado;
};

const createDownloadLink = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ModalVisualizadorDocumentos = ({
  visible,
  documento,
  onClose,
}) => {
  const empresa = useEmpresa();
  
  // Estados para manejo de zoom y arrastre de imágenes
  const [imagenZoom, setImagenZoom] = useState(false);
  const [imagenPosition, setImagenPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [renderKey, setRenderKey] = useState(0);

  // Memoización de valores calculados
  const tipoArchivo = useMemo(() => {
    if (!documento) return FILE_TYPE_LABELS.no_soportado;
    
    // Priorizar MIME type si está disponible
    if (documento.type) {
      return getFileTypeFromMimeType(documento.type);
    }
    
    // Fallback a extensión del archivo
    const extension = documento.name?.split('.').pop()?.toLowerCase() || '';
    return getFileTypeFromExtension(extension);
  }, [documento]);

  const [documentoUrl, setDocumentoUrl] = useState(null);
  const previousUrlRef = React.useRef(null);

  // Recrear URL cuando el documento cambia o el modal se abre
  useEffect(() => {
    // Revocar URL anterior si existe
    if (previousUrlRef.current && previousUrlRef.current.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(previousUrlRef.current);
      } catch (error) {
        // Error al revocar URL anterior
      }
      previousUrlRef.current = null;
    }

    if (!visible || !documento) {
      setDocumentoUrl(null);
      return;
    }

    let url = null;

    // SIEMPRE crear un nuevo ObjectURL desde el File si está disponible
    // Esto asegura que el URL sea válido incluso si el URL anterior fue revocado
    if (documento instanceof File) {
      url = URL.createObjectURL(documento);
      previousUrlRef.current = url;
    } else if (documento.file && documento.file instanceof File) {
      // SIEMPRE crear nuevo URL desde el File, incluso si documento.url existe
      url = URL.createObjectURL(documento.file);
      previousUrlRef.current = url;
    } else if (documento.url && documento.url.startsWith('http')) {
      // Solo usar URL HTTP si no hay File disponible
      url = documento.url;
    } else if (documento.url && documento.url.startsWith('blob:')) {
      // Si no hay File disponible, intentar usar el blob URL (puede no funcionar si fue revocado)
      url = documento.url;
    }

    setDocumentoUrl(url);
    // Forzar re-render del componente de imagen cada vez que se abre el modal
    setRenderKey(prev => prev + 1);
  }, [visible, documento]);

  // Limpiar URL cuando el modal se cierra
  useEffect(() => {
    if (!visible) {
      if (documentoUrl && documentoUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(documentoUrl);
        } catch (error) {
          // Error al revocar URL al cerrar modal
        }
      }
      if (previousUrlRef.current && previousUrlRef.current.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(previousUrlRef.current);
        } catch (error) {
          // Error al revocar previousUrl
        }
        previousUrlRef.current = null;
      }
      setDocumentoUrl(null);
    }
  }, [visible, documentoUrl]);

  // Funciones de utilidad
  const descargarDocumento = useCallback((doc) => {
    try {
      if (doc instanceof File) {
        const url = URL.createObjectURL(doc);
        createDownloadLink(url, doc?.name || 'documento');
        URL.revokeObjectURL(url);
        return;
      }
      
      if (doc.url) {
        createDownloadLink(doc.url, doc?.name || 'documento');
        return;
      }
      
      if (doc.file && doc.file instanceof File) {
        const url = URL.createObjectURL(doc.file);
        createDownloadLink(url, doc?.name || 'documento');
        URL.revokeObjectURL(url);
        return;
      }
    } catch (error) {
      // Error al descargar documento
    }
  }, []);

  // Funciones de manejo de zoom y arrastre
  const alternarZoomImagen = useCallback(() => {
    setImagenZoom(prev => {
      if (!prev) {
        setImagenPosition({ x: 0, y: 0 });
      }
      return !prev;
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (!imagenZoom) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagenPosition.x,
      y: e.clientY - imagenPosition.y
    });
  }, [imagenZoom, imagenPosition]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    if (e.deltaY < 0 && !imagenZoom) {
      setImagenZoom(true);
      setImagenPosition({ x: 0, y: 0 });
    } else if (e.deltaY > 0 && imagenZoom) {
      setImagenZoom(false);
      setImagenPosition({ x: 0, y: 0 });
    }
  }, [imagenZoom]);

  const resetImageState = useCallback(() => {
    setImagenZoom(false);
    setImagenPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const cerrarModal = useCallback(() => {
    resetImageState();
    onClose();
  }, [onClose, resetImageState]);

  // Efectos
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e) => {
      setImagenPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (!visible) {
      resetImageState();
    }
  }, [visible, resetImageState]);


  // Manejo de teclado y scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        cerrarModal();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [visible, cerrarModal]);

  // Componentes de renderizado
  const renderImageError = useCallback(() => (
    <ModalMensaje>
      <ModalIcono>🖼️</ModalIcono>
      <ModalTexto>
        No se pudo cargar la imagen.
        <br />
        Verifica que el archivo esté disponible o descárgalo para verlo.
      </ModalTexto>
      <ModalDescargaBtn
        colorPrincipal={empresa?.COLOR_PRINCIPAL}
        onClick={() => descargarDocumento(documento)}
      >
        Descargar imagen
      </ModalDescargaBtn>
    </ModalMensaje>
  ), [empresa?.COLOR_PRINCIPAL, descargarDocumento, documento]);

  const renderImageFallback = useCallback(() => (
    <ModalMensaje>
      <ModalIcono>🖼️</ModalIcono>
      <ModalTexto>
        No se puede mostrar la imagen.
        <br />
        Puedes descargarla para verla con una aplicación externa.
      </ModalTexto>
      <ModalDescargaBtn
        colorPrincipal={empresa?.COLOR_PRINCIPAL}
        onClick={() => descargarDocumento(documento)}
      >
        Descargar imagen
      </ModalDescargaBtn>
    </ModalMensaje>
  ), [empresa?.COLOR_PRINCIPAL, descargarDocumento, documento]);

  const renderPdfError = useCallback(() => (
    <ModalMensaje>
      <ModalIcono>📄</ModalIcono>
      <ModalTexto>
        No se pudo cargar el PDF.
        <br />
        Verifica que el archivo esté disponible o descárgalo para verlo.
      </ModalTexto>
      <ModalDescargaBtn
        colorPrincipal={empresa?.COLOR_PRINCIPAL}
        onClick={() => descargarDocumento(documento)}
      >
        Descargar PDF
      </ModalDescargaBtn>
    </ModalMensaje>
  ), [empresa?.COLOR_PRINCIPAL, descargarDocumento, documento]);

  const renderPdfFallback = useCallback(() => (
    <ModalMensaje>
      <ModalIcono>📄</ModalIcono>
      <ModalTexto>
        No se puede mostrar el PDF.
        <br />
        Puedes descargarlo para verlo con una aplicación externa.
      </ModalTexto>
      <ModalDescargaBtn
        colorPrincipal={empresa?.COLOR_PRINCIPAL}
        onClick={() => descargarDocumento(documento)}
      >
        Descargar PDF
      </ModalDescargaBtn>
    </ModalMensaje>
  ), [empresa?.COLOR_PRINCIPAL, descargarDocumento, documento]);

  const renderUnsupportedFile = useCallback(() => (
    <ModalMensaje>
      <ModalIcono>❌</ModalIcono>
      <ModalTexto>
        Tipo de archivo no soportado.
        <br />
        Solo se permiten archivos PNG, JPG, JPEG y PDF.
      </ModalTexto>
      <ModalDescargaBtn
        colorPrincipal={empresa?.COLOR_PRINCIPAL}
        onClick={() => descargarDocumento(documento)}
      >
        Descargar archivo
      </ModalDescargaBtn>
    </ModalMensaje>
  ), [empresa?.COLOR_PRINCIPAL, descargarDocumento, documento]);

  const renderImageContent = useCallback(() => {
    if (!documentoUrl) return renderImageFallback();

    return (
      <>
        <ModalImagen
          key={`${documento?.id || documento?.name || 'documento'}-${renderKey}`}
          src={documentoUrl}
          alt={documento?.name || 'Documento'}
          onClick={alternarZoomImagen}
          onMouseDown={handleMouseDown}
          onWheel={handleWheel}
          style={{
            transform: imagenZoom 
              ? `scale(2) translate(${imagenPosition.x}px, ${imagenPosition.y}px)` 
              : 'scale(1) translate(0px, 0px)',
            cursor: imagenZoom 
              ? (isDragging ? 'grabbing' : 'grab') 
              : 'zoom-in',
            transition: isDragging ? 'none' : 'transform 0.3s ease',
            userSelect: 'none',
            position: 'relative',
            zIndex: 1,
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            const errorDiv = e.target.nextSibling;
            if (errorDiv) {
              errorDiv.style.display = 'flex';
            }
          }}
          onLoad={(e) => {
            const errorMessage = e.target.nextSibling;
            if (errorMessage) {
              errorMessage.style.display = 'none';
            }
            e.target.style.display = 'block';
          }}
          title={imagenZoom 
            ? "Hacer clic para reducir • Arrastrar para mover • Rueda del mouse para zoom" 
            : "Hacer clic para ampliar • Rueda del mouse para zoom"
          }
          draggable={false}
        />
        <div style={{ display: 'none' }}>
          {renderImageError()}
        </div>
      </>
    );
  }, [
    documentoUrl, 
    documento?.name,
    documento?.id,
    renderKey,
    alternarZoomImagen, 
    handleMouseDown, 
    handleWheel, 
    imagenZoom, 
    imagenPosition, 
    isDragging, 
    renderImageError, 
    renderImageFallback
  ]);

  const renderPdfContent = useCallback(() => {
    if (!documentoUrl) return renderPdfFallback();

    return (
      <>
        <ModalPdfContainer>
          <ModalPdfIframe
            key={`${documento?.id || documento?.name || 'documento'}-${renderKey}`}
            src={documentoUrl}
            title={documento?.name || 'Documento'}
            onError={(e) => {
              e.target.style.display = 'none';
              const errorDiv = e.target.nextSibling;
              if (errorDiv) {
                errorDiv.style.display = 'flex';
              }
            }}
          />
        </ModalPdfContainer>
        <div style={{ display: 'none' }}>
          {renderPdfError()}
        </div>
      </>
    );
  }, [documentoUrl, documento?.name, documento?.id, renderKey, renderPdfError, renderPdfFallback]);

  const renderContent = useCallback(() => {
    switch (tipoArchivo) {
      case FILE_TYPE_LABELS.imagen:
        return renderImageContent();
      case FILE_TYPE_LABELS.pdf:
        return renderPdfContent();
      case FILE_TYPE_LABELS.no_soportado:
        return renderUnsupportedFile();
      default:
        return null;
    }
  }, [tipoArchivo, renderImageContent, renderPdfContent, renderUnsupportedFile]);

  // Renderizado principal
  if (!visible || !documento) {
    return null;
  }

  return (
    <ModalContainer onClick={cerrarModal}>
      <ModalContenido tipoArchivo={tipoArchivo} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitulo>{documento?.name || 'Documento'}</ModalTitulo>
          <ModalCerrarBtn onClick={cerrarModal} title="Cerrar">
            ×
          </ModalCerrarBtn>
        </ModalHeader>
        
        <ModalDocumentoContainer>
          {renderContent()}
        </ModalDocumentoContainer>
      </ModalContenido>
    </ModalContainer>
  );
};

export default ModalVisualizadorDocumentos;